import { StatsigClient } from '@statsig/js-client';
import { StatsigUser } from '@statsig/client-core';
import { StatsigAutoCapturePlugin } from '@statsig/web-analytics';
import { ConfigurationData, ConfigService } from '../config.service';
import { SessionData } from '../session-data';

/** Statsig event metadata path: metadata.applicationEnvironment.tier */
export const STATSIG_APPLICATION_ENVIRONMENT_TIER_KEY = 'applicationEnvironment.tier';

let cachedApplicationEnvironmentTier: string | undefined;

export function setCachedApplicationEnvironmentTier(tier: string | undefined): void {
  cachedApplicationEnvironmentTier = tier;
}

export function getCachedApplicationEnvironmentTier(): string | undefined {
  return cachedApplicationEnvironmentTier;
}

export function resolveApplicationEnvironmentTier(config?: ConfigurationData | null): string | undefined {
  if (!config) {
    return undefined;
  }

  const camel = config.applicationEnvironment?.tier;
  if (camel) {
    return camel;
  }

  const pascal = (config as { ApplicationEnvironment?: { Tier?: string } }).ApplicationEnvironment?.Tier;
  return pascal || undefined;
}

export function getApplicationEnvironmentTier(configService?: ConfigService): string | undefined {
  return getCachedApplicationEnvironmentTier() ?? resolveApplicationEnvironmentTier(configService?.getConfigData());
}

export function buildStatsigCustomIDs(sd?: SessionData): Record<string, string> {
  const customIDs: Record<string, string> = { platform: 'NG Car UI' };
  if (sd?.memberId) customIDs['memberID'] = sd.memberId;
  if (sd?.partnerId) customIDs['partnerID'] = sd.partnerId;
  if (sd?.nameId) customIDs['nameID'] = sd.nameId;
  if (sd?.memberclassid) customIDs['memberClassID'] = sd.memberclassid;
  else if (sd?.identity?.ice_member_class_id) customIDs['memberClassID'] = sd.identity.ice_member_class_id;
  if (sd?.agentId) customIDs['agentID'] = sd.agentId;
  return customIDs;
}

export function resolveStatsigUserID(
  sd?: SessionData,
  customIDs?: Record<string, string>,
  user?: StatsigUser
): string | undefined {
  return sd?.memberId
    || customIDs?.memberID
    || user?.userID
    || user?.customIDs?.memberID;
}

export function buildStatsigUser(sd: SessionData | undefined): StatsigUser {
  const customIDs = buildStatsigCustomIDs(sd);
  const userID = resolveStatsigUserID(sd, customIDs);

  return {
    customIDs,
    ...(userID ? { userID } : {})
  };
}

export function buildStatsigInitOptions(tier?: string): {
  plugins: StatsigAutoCapturePlugin[];
  environment?: { tier: string };
} {
  return {
    plugins: [new StatsigAutoCapturePlugin()],
    ...(tier ? { environment: { tier } } : {})
  };
}

export function buildStatsigEventEnvironmentMetadata(tier?: string): Record<string, string> {
  if (!tier) {
    return {};
  }

  return {
    [STATSIG_APPLICATION_ENVIRONMENT_TIER_KEY]: tier
  };
}

function applyTierToEventRecord(
  event: { metadata?: Record<string, unknown> },
  tier: string
): void {
  const metadata = event.metadata ?? {};
  metadata[STATSIG_APPLICATION_ENVIRONMENT_TIER_KEY] = tier;
  event.metadata = metadata;
}

/** Ensures tier is on event metadata for autocapture and custom events before flush. */
export function registerStatsigApplicationEnvironmentOnEvents(client: StatsigClient): void {
  client.on('log_event_called', ({ event }) => {
    const tier = getCachedApplicationEnvironmentTier();
    if (!tier || !event) {
      return;
    }
    applyTierToEventRecord(
      event as { metadata?: Record<string, unknown> },
      tier
    );
  });

  client.on('pre_logs_flushed', ({ events }) => {
    const tier = getCachedApplicationEnvironmentTier();
    if (!tier) {
      return;
    }

    for (const event of events as Array<{ metadata?: Record<string, unknown> }>) {
      applyTierToEventRecord(event, tier);
    }
  });
}
